import * as React from "react";
import { useCallback } from "react";

import { withResolvePaths } from "../../withResolvePaths";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { useMultilineClient } from "../../../Engine/Core/Filtering/UseMultilineClient";
import {
    UserFriendlyLoadingState,
    useUserFriendlyLoadingState,
} from "../UserPicklistDeclaration/SelectNidusEntryModal/SelectEntryModal/UserFriendlyLoadingState";
import { createHocDisplayName } from "../../TypingUtils";

import { Pager, PagerProps } from "./Pager";

interface PagerContainerOwnProps {
    filteredUniqKey: string;
    bindingPath: AbsoluteResolvedModelPath;
}

export type PagerContainerProps = PagerContainerOwnProps & Omit<PagerProps, keyof ReturnType<typeof usePagerProps>>;

const usePagerProps = (ownProps: PagerContainerOwnProps) => {
    const { filteredUniqKey, bindingPath } = ownProps;
    const { client, instancesPerPage, instanceCount, currentPage, loading } = useMultilineClient(
        bindingPath,
        filteredUniqKey,
        info => ({
            client: info.client,
            instancesPerPage: info.multilineState.instancesPerPage,
            currentPage: info.multilineState.currentPage,
            instanceCount: info.multilineState.displayedInstances.length,
            loading: info.multilineState.loading,
        })
    );

    const handleGoToPage = useCallback(
        (pageNumber: number) => {
            client.setCurrentPage(pageNumber);
        },
        [client]
    );
    const userFriendlyLoading = useUserFriendlyLoadingState(loading) > UserFriendlyLoadingState.LoadingState1;

    return {
        currentPage: currentPage,
        instancesPerPage: instancesPerPage,
        instanceCount: instanceCount,
        onGoToPage: handleGoToPage,
        loading: userFriendlyLoading,
    };
};

const WithPagerContainerWrapper = (ownProps: PagerContainerProps) => {
    const { currentPage, onGoToPage, instancesPerPage, instanceCount, loading } = usePagerProps(ownProps);
    return (
        <Pager
            {...ownProps}
            currentPage={currentPage}
            instancesPerPage={instancesPerPage}
            instanceCount={instanceCount}
            onGoToPage={onGoToPage}
            loading={loading}
        />
    );
};
WithPagerContainerWrapper.displayName = createHocDisplayName(Pager, "PagerContainerWrapper");
export const PagerContainer = React.memo(withResolvePaths(WithPagerContainerWrapper));
